
// src/pages/DifferentialCdsLeader/components/GroupManager.jsx
import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Row,
  Col,
  Button,
  Input,
  Badge,
  Alert,
} from "reactstrap";
import { getAvailableFiles, getGroupFiles } from "../../../utils/groupUtils";

const GroupManager = ({
  groups = [],
  allFiles = [],
  filteredFiles = [],
  onAddFilesToGroup,
  onRemoveFilesFromGroup,
  onUpdateGroupName,
  onClearGroup,
  onAutoFill,
  onAutoLabel,
  onClearAll,
  groupLabels = {},
  onUpdateGroupLabel,
}) => {
  const [draggedFile, setDraggedFile] = useState(null);
  const [draggedFileSourceGroup, setDraggedFileSourceGroup] = useState(null);
  const [dragOverGroup, setDragOverGroup] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);

  // Memoized available files calculation
  const availableFiles = useMemo(() => {
    return getAvailableFiles(filteredFiles, groups);
  }, [filteredFiles, groups]);

  const minCellWidth = useMemo(() => {
    if (availableFiles.length === 0) return 180;
      const longestName = Math.max(...availableFiles.map(f => f.file_name.length));
      const calculated = longestName * 7 + 40;
    return Math.min(Math.max(calculated, 180), 280);
  }, [availableFiles]);
    useEffect(() => {
      if (Array.isArray(allFiles) && allFiles.length > 0) {
        window.globalFileCache = allFiles;
      }
  }, [allFiles]);

  // Memoized group files function
  const getGroupFilesWithDetails = useCallback(
    (groupId) => {
      return getGroupFiles(groupId, groups, allFiles);
    },
    [groups, allFiles]
  );

  // Drag handlers with proper cleanup
  const handleDragStart = useCallback(
    (e, fileId) => {
      if (selectedFiles.includes(fileId)) {
        setDraggedFile(selectedFiles);
      } else {
        setDraggedFile([fileId]);
      }
      setDraggedFileSourceGroup(null);
      e.dataTransfer.effectAllowed = "move";
    },
    [selectedFiles]
  );

  const handleGroupFileDragStart = useCallback((e, fileId, sourceGroupId) => {
    e.stopPropagation();
    setDraggedFile([fileId]);
    setDraggedFileSourceGroup(sourceGroupId);
    e.dataTransfer.effectAllowed = "move";
  }, []);

  const handleDragOver = useCallback((e, groupId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverGroup(groupId);
  }, []);

  const handleDragLeave = useCallback((e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverGroup(null);
    }
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggedFile(null);
    setDraggedFileSourceGroup(null);
    setDragOverGroup(null);
  }, []);

  const handleDrop = useCallback(
    (e, targetGroupId) => {
      e.preventDefault();
      if (draggedFile) {
        const filesToMove = Array.isArray(draggedFile)
          ? draggedFile
          : [draggedFile];

        if (draggedFileSourceGroup) {
          if (draggedFileSourceGroup !== targetGroupId) {
            onRemoveFilesFromGroup(draggedFileSourceGroup, filesToMove);
            onAddFilesToGroup(targetGroupId, filesToMove);
          }
        } else {
          onAddFilesToGroup(targetGroupId, filesToMove);
          setSelectedFiles([]);
        }

        setDraggedFile(null);
        setDraggedFileSourceGroup(null);
      }
      setDragOverGroup(null);
    },
    [
      draggedFile,
      draggedFileSourceGroup,
      onAddFilesToGroup,
      onRemoveFilesFromGroup,
    ]
  );

  const handleFileClick = useCallback(
    (e, fileId) => {
      e.preventDefault();
      const isAssigned = groups.some((g) => (g.files || []).includes(fileId));
      if (isAssigned) return;

      const currentIndex = availableFiles.findIndex(
        (f) => f.file_id === fileId
      );

      if (e.shiftKey && selectedFiles.length > 0) {
        const lastSelectedIndex = availableFiles.findIndex(
          (f) => f.file_id === selectedFiles[selectedFiles.length - 1]
        );

        if (lastSelectedIndex !== -1 && currentIndex !== -1) {
          const start = Math.min(lastSelectedIndex, currentIndex);
          const end = Math.max(lastSelectedIndex, currentIndex);
          const rangeFiles = availableFiles
            .slice(start, end + 1)
            .map((f) => f.file_id);

          const allInRangeSelected = rangeFiles.every((id) =>
            selectedFiles.includes(id)
          );

          if (allInRangeSelected) {
            setSelectedFiles((prev) =>
              prev.filter((id) => !rangeFiles.includes(id))
            );
          } else {
            setSelectedFiles((prev) => [...new Set([...prev, ...rangeFiles])]);
          }
        }
      } else {
        setSelectedFiles((prev) =>
          prev.includes(fileId)
            ? prev.filter((id) => id !== fileId)
            : [...prev, fileId]
        );
      }
    },
    [groups, selectedFiles, availableFiles]
  );

  const handleSelectAll = useCallback(() => {
    if (selectedFiles.length === availableFiles.length) {
      setSelectedFiles([]);
    } else {
      setSelectedFiles(availableFiles.map((f) => f.file_id));
    }
  }, [selectedFiles.length, availableFiles]);

  const getBackgroundColor = useCallback((color) => {
    const colorMap = {
      "#1a6d51": "#E8F5E8",
      "#dc3545": "#FDEDEE",
    };
    return colorMap[color] || "#F5F5F5";
  }, []);

  const handleFileMouseEnter = useCallback((e, isAssigned, isSelected) => {
    if (!isAssigned && !isSelected) {
      e.currentTarget.style.backgroundColor = "#f9fafb";
      e.currentTarget.style.borderColor = "#d1d5db";
    }
  }, []);

  const handleFileMouseLeave = useCallback((e, isAssigned, isSelected) => {
    if (!isAssigned && !isSelected) {
      e.currentTarget.style.backgroundColor = "white";
      e.currentTarget.style.borderColor = "#e5e7eb";
    }
  }, []);

  // Memoized display groups with your requested colors
  const displayGroups = useMemo(
    () => [
      {
        id: "group1",
        name: groupLabels?.group1 || "Group 1",
        color: "#1a6d51",
        files: getGroupFilesWithDetails("group1"),
      },
      {
        id: "group2",
        name: groupLabels?.group2 || "Group 2",
        color: "#dc3545",
        files: getGroupFilesWithDetails("group2"),
      },
    ],
    [groupLabels, getGroupFilesWithDetails]
  );

  return (
    <Card
      className="mb-4"
      style={{
        border: "1px solid #e5e7eb",
        borderRadius: "1px",
        height: "100%",
        minHeight: "400px",
      }}
    >
      <CardHeader>
        <div className="d-flex justify-content-between align-items-center mb-1">
          <div>
            <h5
              className="mb-0"
              style={{ color: "#1a6d51", fontSize: "18px", fontWeight: "600" }}
            >
              <i className="ri-apps-2-line me-2"></i> Groups
            </h5>
          </div>

          <div className="d-flex gap-2">
            {onClearAll && (
              <Button
                size="sm"
                onClick={onClearAll}
                style={{
                  backgroundColor: "transparent",
                  border: "1px solid #dc3545",
                  color: "#dc3545",
                  fontSize: "12px",
                  padding: "6px 12px",
                  borderRadius: "6px",
                }}
              >
                <i className="ri-delete-bin-line me-1"></i>
                Clear All
              </Button>
            )}
            {onAutoLabel && (
              <Button
                size="sm"
                onClick={() => onAutoLabel(allFiles)}
                style={{
                  backgroundColor: "#28a745",
                  borderColor: "#28a745",
                  color: "white",
                  fontSize: "12px",
                  padding: "6px 12px",
                  borderRadius: "6px",
                }}
              >
                <i className="ri-price-tag-3-line me-2"></i>
                Auto Label
              </Button>
            )}
            <Button
              size="sm"
              onClick={handleSelectAll}
              disabled={availableFiles.length === 0}
              style={{
                backgroundColor: "#28a745",
                borderColor: "#28a745",
                color: "white",
                fontSize: "12px",
                padding: "6px 12px",
                borderRadius: "6px",
              }}
            >
              <i className=" ri-arrow-right-up-fill me-2"></i>
              {selectedFiles.length === availableFiles.length &&
              availableFiles.length > 0
                ? "Deselect All"
                : "Select All"}
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardBody style={{ padding: "20px" }}>
        {/* Available Files Section */}
        <div className="mb-4">
          {availableFiles.length > 0 && (
            <div
              className="text-center mb-2"
              style={{
                background:
                  "linear-gradient(135deg, #1a6d51 0%, #29a161ff 100%)",
                color: "white",
                borderRadius: "6px",
                padding: "4px 12px",
                fontSize: "11px",
                display: "inline-block",
              }}
            >
              <i
                className="ri-cursor-line me-1"
                style={{ fontSize: "12px" }}
              ></i>
              Click files to select/deselect them. Use{" "}
              <kbd
                style={{
                  fontSize: "9px",
                  padding: "1px 3px",
                  backgroundColor: "rgba(255,255,255,0.2)",
                  border: "none",
                }}
              >
                Shift
              </kbd>
              +Click for range selection. Drag files to groups.
            </div>
          )}

          <div
            style={{
              overflowY: "auto",
              border: "1px solid #e5e7eb",
              borderRadius: "6px",
              padding: "8px",
              backgroundColor: "#fafafa",
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: `repeat(auto-fill, minmax(${minCellWidth}px, 1fr))`,
                gap: "6px",
              }}
            >
              {availableFiles.map((file, index) => {
                const isAssigned = groups.some((g) =>
                  g.files.includes(file.file_id)
                );
                const isSelected = selectedFiles.includes(file.file_id);

                return (
                  <div
                    key={file.file_id}
                    draggable={!isAssigned}
                    onDragStart={(e) =>
                      !isAssigned && handleDragStart(e, file.file_id)
                    }
                    onClick={(e) => handleFileClick(e, file.file_id)}
                    onMouseEnter={(e) =>
                      handleFileMouseEnter(e, isAssigned, isSelected)
                    }
                    onMouseLeave={(e) =>
                      handleFileMouseLeave(e, isAssigned, isSelected)
                    }
                    style={{
                      padding: "6px",
                      border: isSelected
                        ? "1px solid #2F9E37"
                        : "1px solid #e5e7eb",
                      borderRadius: "4px",
                      backgroundColor: isAssigned
                        ? "#f3f4f6"
                        : isSelected
                        ? "#f0fdf4"
                        : "white",
                      cursor: isAssigned ? "not-allowed" : "pointer",
                      fontSize: "12px",
                      opacity: isAssigned ? 0.7 : 1,
                      transition: "all 0.2s ease",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      userSelect: "none",
                    }}
                  >
                    <Input
                      type="checkbox"
                      checked={isSelected && !isAssigned}
                      readOnly
                      style={{
                        pointerEvents: "none",
                        margin: 0,
                        width: "14px",
                        height: "14px",
                      }}
                    />
                    <span
                      style={{
                        fontSize: "11px",
                        lineHeight: "1.2",
                        overflow: "hidden",
                        flex: 1,
                        color: isAssigned ? "#9ca3af" : "#374151",
                      }}
                      title={file.file_name}
                    >
                      {file.file_name}
                    </span>
                    {isAssigned && (
                      <Badge
                        style={{
                          fontSize: "8px",
                          backgroundColor: "#9ca3af",
                          padding: "2px 4px",
                        }}
                      >
                        Used
                      </Badge>
                    )}
                  </div>
                );
              })}

              {availableFiles.length === 0 && (
                <div
                  className="text-muted text-center"
                  style={{
                    gridColumn: "1 / -1",
                    padding: "20px",
                    fontSize: "14px",
                  }}
                >
                  All files have been assigned to groups
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Quick Assign Bar */}
        {selectedFiles.length > 0 && (
          <div className="mb-4 p-3 bg-light rounded">
            <div className="d-flex align-items-center justify-content-between">
              <span className="fw-bold">
                Assign {selectedFiles.length} selected file(s) to:
              </span>
              <div className="d-flex gap-2">
                {displayGroups.map((group) => (
                  <Button
                    key={group.id}
                    size="sm"
                    style={{
                      backgroundColor: group.color,
                      borderColor: group.color,
                      color: "white",
                    }}
                    onClick={() => {
                      onAddFilesToGroup(group.id, selectedFiles);
                      setSelectedFiles([]);
                    }}
                  >
                    {group.name}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Groups Grid */}
        <Row>
          {displayGroups.map((group) => (
            <Col key={group.id} xl={6} lg={6} md={6} className="mb-3">
              <Card
                style={{
                  backgroundColor: getBackgroundColor(group.color),
                  border:
                    dragOverGroup === group.id
                      ? `2px dashed ${group.color}`
                      : `1px solid ${group.color}`,
                  borderRadius: "8px",
                  minHeight: "120px",
                  transition: "all 0.2s ease",
                  position: "relative",
                  transform:
                    dragOverGroup === group.id ? "scale(1.02)" : "scale(1)",
                  boxShadow:
                    dragOverGroup === group.id
                      ? `0 4px 12px rgba(0,0,0,0.15), 0 0 0 4px ${group.color}20`
                      : "none",
                  opacity:
                    draggedFileSourceGroup === group.id && !dragOverGroup
                      ? 0.7
                      : 1,
                }}
                onDragOver={(e) => handleDragOver(e, group.id)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, group.id)}
              >
                <CardBody style={{ padding: "20px" }}>
                  {/* Group Header */}
                  <div className="d-flex justify-content-between align-items-center mb-3 w-100">
                    <div className="d-flex align-items-center flex-grow-1">
                      <div
                        style={{
                          width: "16px",
                          height: "16px",
                          borderRadius: "50%",
                          backgroundColor: group.color,
                          marginRight: "8px",
                        }}
                      />
                      <Input
                        type="text"
                        value={group.name}
                        onChange={(e) =>
                          onUpdateGroupLabel?.(group.id, e.target.value)
                        }
                        style={{
                          fontWeight: "600",
                          fontSize: "14px",
                          padding: "4px",
                          color: "#333",
                          flex: 1,        
                          width: "100%",    
                          maxWidth: "none",
                          marginRight: "10%",
                          boxShadow: "none",
                        }}
                        placeholder={`Group ${
                          group.id === "group1" ? "1" : "2"
                        }`}
                      />
                    </div>
                    {/* Clear Group Button */}
                    <Button
                      size="sm"
                      onClick={() => onClearGroup?.(group.id)}
                      style={{
                        backgroundColor: "transparent",
                        border: "1px solid #f59e0b",
                        color: "#f59e0b",
                        fontSize: "10px",
                        padding: "4px 8px",
                        fontWeight: "500",
                        borderRadius: "4px",
                      }}
                    >
                      <i className="ri-delete-bin-line me-1"></i>
                      Clear
                    </Button>
                  </div>

                  {/* Files or Drop Zone */}
                  {group.files.length === 0 ? (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: "100px",
                        border: "2px dashed #ccc",
                        borderRadius: "8px",
                        backgroundColor: "rgba(255,255,255,0.5)",
                        color: "#999",
                        fontSize: "12px",
                        textAlign: "center",
                      }}
                    >
                      <i
                        className="ri-upload-cloud-2-line"
                        style={{ fontSize: "24px", marginBottom: "8px" }}
                      ></i>
                      <div>Drag files here</div>
                      <div style={{ fontSize: "10px", marginTop: "4px" }}>
                        {selectedFiles.length > 0
                          ? `${selectedFiles.length} files selected`
                          : "Select files to drag"}
                      </div>
                    </div>
                  ) : (
                    <div style={{ marginTop: "8px" }}>
                      <div
                        style={{
                          minHeight: "100px",
                          overflowY: "auto",
                        }}
                      >
                        {group.files.map((file) => {
                          if (!file) return null;
                          const isDragging =
                            draggedFile && draggedFile.includes(file.file_id);
                          return (
                            <div
                              key={file.file_id}
                              className="d-flex justify-content-between align-items-center mb-2"
                              style={{
                                padding: "2px 12px",
                                backgroundColor: isDragging
                                  ? "rgba(255,255,255,0.5)"
                                  : "rgba(255,255,255,0.8)",
                                borderRadius: "6px",
                                fontSize: "12px",
                                border: "1px solid rgba(255,255,255,0.5)",
                                cursor: "grab",
                                transition: "all 0.2s ease",
                                opacity: isDragging ? 0.5 : 1,
                                transform: isDragging
                                  ? "scale(0.95)"
                                  : "scale(1)",
                              }}
                              draggable={true}
                              onDragStart={(e) =>
                                handleGroupFileDragStart(
                                  e,
                                  file.file_id,
                                  group.id
                                )
                              }
                              onDragEnd={handleDragEnd}
                              onMouseEnter={(e) => {
                                if (!isDragging) {
                                  e.currentTarget.style.cursor = "grabbing";
                                }
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.cursor = "grab";
                              }}
                            >
                              <span
                                style={{
                                  fontWeight: "500",
                                  color: "#333",
                                  overflow: "hidden",
                                  flex: 1,
                                }}
                              >
                                {file.file_name}
                              </span>
                              <Button
                                size="sm"
                                onClick={() =>
                                  onRemoveFilesFromGroup?.(group.id, [
                                    file.file_id,
                                  ])
                                }
                                style={{
                                  backgroundColor: "transparent",
                                  border: "none",
                                  color: "#dc3545",
                                  padding: "2px 4px",
                                  fontSize: "10px",
                                  marginLeft: "8px",
                                }}
                              >
                                <i className="ri-close-line"></i>
                              </Button>
                            </div>
                          );
                        })}
                      </div>

                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>
      </CardBody>
    </Card>
  );
};

export default GroupManager;
